import { ADD_OPS, DIFF_OPS } from "./source-field.constants";
import { computeFormValues } from "./computed-field.utils";

const isAdd = (formData) => ADD_OPS.includes(formData.op);
const isDiff = (formData) => DIFF_OPS.includes(formData.op);
const fromCompute = (key) => (record) => computeFormValues(record.compute)[key];
const option = (dependencies, key, id) => (dependencies?.[key] || []).find((item) => item.id === id);

// EntityFormModal fields of a computed field. Each input shows only for the operation that reads it;
// computed-field.utils.js turns the flat values into the `compute` object.
export const computedFieldFields = [
  { name: "op", label: "Operation", type: "select", is_api_data: true, optionsKey: "ops", optionLabel: "label", required: true, editValue: fromCompute("op") },

  { name: "base", label: "Date to add to", type: "select", is_api_data: true, optionsKey: "dateColumns", optionLabel: "label", hidden: (f) => !isAdd(f), editValue: fromCompute("base") },
  { name: "amount_mode", label: "Amount comes from", type: "select", is_api_data: true, optionsKey: "amountModes", optionLabel: "label", hidden: (f) => !isAdd(f), editValue: fromCompute("amount_mode") },
  {
    name: "amount",
    label: "Amount",
    type: "number",
    min: -1000,
    max: 1000,
    hint: "Whole number from -1000 to 1000; negative goes back in time.",
    hidden: (f) => !isAdd(f) || f.amount_mode === "field",
    editValue: fromCompute("amount"),
  },
  { name: "amount_path", label: "Number column", type: "select", is_api_data: true, optionsKey: "numberColumns", optionLabel: "label", hidden: (f) => !isAdd(f) || f.amount_mode !== "field", editValue: fromCompute("amount_path") },

  { name: "from", label: "From", type: "select", is_api_data: true, optionsKey: "fromColumns", optionLabel: "label", hidden: (f) => !isDiff(f), editValue: fromCompute("from") },
  { name: "to", label: "To (empty = today)", type: "select", is_api_data: true, optionsKey: "fromColumns", optionLabel: "label", isClearable: true, hidden: (f) => !isDiff(f), editValue: fromCompute("to") },

  { name: "relation", label: "Related records to count", type: "select", is_api_data: true, optionsKey: "listRelations", optionLabel: "label", hidden: (f) => f.op !== "count_relation", editValue: fromCompute("relation") },
  { name: "where_text", label: "Only count where (Prisma where JSON, optional)", type: "textarea", rows: 3, fullWidth: true, hidden: (f) => f.op !== "count_relation", editValue: fromCompute("where_text") },

  {
    name: "latest_column",
    label: "Column of the newest related record",
    type: "select",
    is_api_data: true,
    optionsKey: "latestColumns",
    optionLabel: "label",
    hidden: (f) => f.op !== "latest_value",
    editValue: fromCompute("latest_column"),
    // Keep the column's type (it is the result type) and offer "id" of the same relation as the order.
    onValueChange: (value, formData, dependencies) => {
      const relation = String(value).split(".")[0];
      const order = String(formData.order_by ?? "").split(".")[0] === relation ? formData.order_by : `${relation}.id`;
      return { latest_type: option(dependencies, "latestColumns", value)?.data_type ?? "", order_by: order };
    },
  },
  { name: "order_by", label: "Newest by", type: "select", is_api_data: true, optionsKey: "latestOrderColumns", optionLabel: "label", hidden: (f) => f.op !== "latest_value", editValue: fromCompute("order_by") },
  { name: "default_value", label: "Value when there is none", hint: "Optional, e.g. TO_DO.", hidden: (f) => f.op !== "latest_value", editValue: fromCompute("default_value") },
  { name: "latest_type", label: "Result type", hidden: () => true, editValue: (record) => (record.compute?.op === "latest_value" ? record.data_type : "") },

  { name: "field_key", label: "Field key", required: true, maxLength: 63, hint: "Used in conditions and templates, e.g. days_to_deadline." },
  { name: "label", label: "Label", required: true, maxLength: 100 },
  { name: "field_role", label: "Role", type: "select", is_api_data: true, optionsKey: "roles", optionLabel: "label", isClearable: true },
  { name: "format", label: "Format", type: "select", is_api_data: true, optionsKey: "formats", optionLabel: "label", isClearable: true },
  { name: "sort_order", label: "Sort order", type: "number", min: 0, max: 10000 },
  { name: "is_filterable", label: "Can be filtered", type: "boolean", defaultValue: true, editValue: (record) => record.is_filterable === true },
  { name: "is_template_var", label: "Template variable", type: "boolean", defaultValue: true, editValue: (record) => record.is_template_var === true },
  { name: "description", label: "Description", type: "textarea", fullWidth: true, rows: 2, maxLength: 500 },
];
